import React, { useState } from 'react';
import { StockCategory, StockItem } from '../types';
import { formatCurrencyValue } from '../utils/storage';
import { 
  Plus, 
  Settings, 
  Layers, 
  Trash2, 
  X, 
  Edit, 
  Sparkles, 
  Search,
  ArrowRightLeft,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  ArrowUpCircle,
  ArrowDownCircle
} from 'lucide-react';
import { Skeleton, TableSkeleton } from './Skeleton';

interface StockProps {
  stockItems: StockItem[];
  stockCategories: StockCategory[];
  onAddStockItem: (item: Omit<StockItem, 'id'>) => void;
  onUpdateStockItem: (id: string, item: Partial<StockItem>) => void;
  onAddStockItemBulk: (items: Omit<StockItem, 'id'>[]) => void;
  onAddCategory: (name: string) => void;
  onUpdateCategory: (id: string, active: boolean) => void;
  isAdmin: boolean;
  isLoading?: boolean;
}

interface NewItemRow {
  categoryId: string;
  name: string;
  quantity: string;
  price: string;
  minLimit: string;
}

export default function Stock({ 
  stockItems, 
  stockCategories, 
  onAddStockItem, 
  onUpdateStockItem, 
  onAddStockItemBulk,
  onAddCategory, 
  onUpdateCategory,
  isAdmin,
  isLoading = false
}: StockProps) {
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  // Search/Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('todas');

  // Category addition
  const [newCatName, setNewCatName] = useState('');
  const [catError, setCatError] = useState('');

  // Bulk add items form state
  const [newItemRows, setNewItemRows] = useState<NewItemRow[]>([
    { categoryId: '', name: '', quantity: '', price: '' }
  ]);
  const [modalError, setModalError] = useState('');

  // Main table transaction states (entries/exits typed by user)
  // keyed by itemId: { addQuantity: string, subQuantity: string, motive: string }
  const [txStates, setTxStates] = useState<Record<string, { addQty: string; subQty: string; motive: string }>>({});

  const handleAddCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim()) {
      setCatError('Insira um nome válido para a categoria.');
      return;
    }
    const dup = stockCategories.some(c => c.name.toLowerCase() === newCatName.trim().toLowerCase());
    if (dup) {
      setCatError('Esta categoria já existe no estoque.');
      return;
    }
    onAddCategory(newCatName.trim());
    setNewCatName('');
    setCatError('');
  };

  // Open item modal
  const openAddItemModal = () => {
    const activeCats = stockCategories.filter(c => c.active);
    if (activeCats.length === 0) {
      alert("Por favor, adicione pelo menos uma categoria ativa de estoque primeiro!");
      setShowCategoryModal(true);
      return;
    }

    setNewItemRows([
      { categoryId: activeCats[0].id, name: '', quantity: '10', price: '5.00', minLimit: '5' }
    ]);
    setModalError('');
    setShowAddItemModal(true);
  };

  const handleAddNewRowInModal = () => {
    const activeCats = stockCategories.filter(c => c.active);
    setNewItemRows(prev => [
      ...prev,
      { categoryId: activeCats[0]?.id || '', name: '', quantity: '10', price: '5.00', minLimit: '5' }
    ]);
  };

  const handleRemoveRowInModal = (idx: number) => {
    if (newItemRows.length === 1) return;
    setNewItemRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleItemRowChange = (idx: number, field: keyof NewItemRow, val: string) => {
    setNewItemRows(prev => prev.map((row, i) => {
      if (i === idx) {
        return { ...row, [field]: val };
      }
      return row;
    }));
  };

  const handleSubmitBulkItems = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate rows
    const validated: Omit<StockItem, 'id'>[] = [];
    for (let i = 0; i < newItemRows.length; i++) {
      const { categoryId, name, quantity, price, minLimit } = newItemRows[i];
      if (!name.trim()) {
        setModalError(`O nome do item na linha ${i + 1} está em branco.`);
        return;
      }
      const qtyNum = parseInt(quantity);
      const prcNum = parseFloat(price);
      const minLmtNum = parseInt(minLimit);
      if (isNaN(qtyNum) || qtyNum < 0) {
        setModalError(`A quantidade na linha ${i + 1} deve ser um número maior ou igual a zero.`);
        return;
      }
      if (isNaN(prcNum) || prcNum < 0) {
        setModalError(`O valor na linha ${i + 1} deve ser maior ou igual a zero.`);
        return;
      }
      if (isNaN(minLmtNum) || minLmtNum < 0) {
        setModalError(`O limite mínimo na linha ${i + 1} deve ser um número maior ou igual a zero.`);
        return;
      }
      validated.push({
        categoryId,
        name: name.trim(),
        quantity: qtyNum,
        price: prcNum,
        minLimit: minLmtNum,
        active: true
      });
    }

    onAddStockItemBulk(validated);
    setShowAddItemModal(false);
  };

  // Modify quick adjustments in the main table
  const handleTxChange = (itemId: string, field: 'addQty' | 'subQty' | 'motive', val: string) => {
    setTxStates(prev => {
      const current = prev[itemId] || { addQty: '', subQty: '', motive: '' };
      return {
        ...prev,
        [itemId]: { ...current, [field]: val }
      };
    });
  };

  // Commit entries (Entradas) in bulk
  const commitEntries = () => {
    let count = 0;
    const itemsWithEntries = (Object.entries(txStates) as [string, { addQty: string; subQty: string; motive: string }][]).filter(([_, state]) => state.addQty.trim() !== '' && parseInt(state.addQty) > 0);
    
    if (itemsWithEntries.length === 0) {
      alert("Por favor, informe a quantidade a adicionar em pelo menos um item.");
      return;
    }

    itemsWithEntries.forEach(([itemId, state]) => {
      const item = stockItems.find(i => i.id === itemId);
      if (item) {
        const addedValue = parseInt(state.addQty);
        onUpdateStockItem(itemId, { quantity: item.quantity + addedValue });
        count++;
      }
    });

    // Clear entries
    setTxStates({});
    alert(`Entrada realizada com sucesso em ${count} item(ns).`);
  };

  // Commit withdrawals (Saídas) in bulk
  const commitWithdrawals = () => {
    let count = 0;
    const itemsWithWithdrawals = (Object.entries(txStates) as [string, { addQty: string; subQty: string; motive: string }][]).filter(([_, state]) => state.subQty.trim() !== '' && parseInt(state.subQty) > 0);

    if (itemsWithWithdrawals.length === 0) {
      alert("Por favor, informe a quantidade a retirar em pelo menos um item.");
      return;
    }

    // Validation checks
    for (const [itemId, state] of itemsWithWithdrawals) {
      const item = stockItems.find(i => i.id === itemId);
      if (!item) continue;
      const subbed = parseInt(state.subQty);
      if (subbed > item.quantity) {
        alert(`Erro: Quantidade de retirada (${subbed}) é maior que o saldo atual de ${item.name} (${item.quantity}).`);
        return;
      }
    }

    itemsWithWithdrawals.forEach(([itemId, state]) => {
      const item = stockItems.find(i => i.id === itemId);
      if (item) {
        const subbed = parseInt(state.subQty);
        onUpdateStockItem(itemId, { quantity: item.quantity - subbed });
        count++;
      }
    });

    setTxStates({});
    alert(`Saída de item(ns) realizada com sucesso para ${count} produto(s).`);
  };

  // Filters logic
  const filteredProducts = stockItems.filter(item => {
    const safeName = item.name || '';
    const matchesSearch = safeName.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'todas' || item.categoryId === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-stock" className="text-xl font-semibold tracking-tight text-zinc-950">
            Estoque Central de Insumos
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Gerencie o estoque geral de alimentos, bebidas e itens de higiene. Transfira produtos para os frigobares das acomodações.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            id="btn-manage-categories-trigger"
            onClick={() => {
              setNewCatName('');
              setCatError('');
              setShowCategoryModal(true);
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 border border-zinc-200 hover:border-zinc-300 rounded-lg text-zinc-700 bg-white font-semibold text-xs cursor-pointer shadow-sm"
          >
            <Layers className="w-4 h-4 text-zinc-500" /> Categorias
          </button>
          
          <button
            type="button"
            id="btn-add-stock-item-trigger"
            onClick={openAddItemModal}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" /> Adicionar Item
          </button>
        </div>
      </div>

      {/* Categories Search Selector bar */}
      <div className="bg-white p-4 border border-zinc-200 rounded-xl shadow-sm flex flex-col md:flex-row items-center gap-4">
        <div className="relative w-full md:flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400">
            <Search className="w-4 h-4" />
          </div>
          <input
            type="text"
            id="search-stock-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-colors"
            placeholder="Buscar por nome do produto..."
          />
        </div>

        <div className="flex items-center gap-2 self-stretch md:self-auto">
          <span className="text-xxs font-bold text-zinc-400 uppercase tracking-wider">Filtrar Categoria:</span>
          <select
            id="select-category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-xs border border-zinc-200 rounded-lg p-2 bg-zinc-50 font-semibold focus:outline-none"
          >
            <option value="todas">Todas as Categorias</option>
            {stockCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Stock Table Container */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table id="table-stock-transactions" className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                <th className="py-3 px-4 w-12 text-center">#</th>
                <th className="py-3 px-4">Item (Produto)</th>
                <th className="py-3 px-4">Categoria</th>
                <th className="py-3 px-4">Qtd Atual</th>
                <th className="py-3 px-4">Adicionar (+ de Entrada)</th>
                <th className="py-3 px-4">Retirar (- de Saída)</th>
                <th className="py-3 px-4">Qtd Futura (Cálculo)</th>
                <th className="py-3 px-4">Motivo (Saída)</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right w-16">Acção</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="p-4">
                    <TableSkeleton rows={5} />
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={10} id="stock-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                    <p className="mb-3">Nenhum insumo ou item de estoque correspondente.</p>
                    <button onClick={openAddItemModal} className="px-4 py-2 bg-zinc-900 text-white text-xs font-bold rounded-lg cursor-pointer">Adicionar primeiro item</button>
                  </td>
                </tr>
              ) : (
                filteredProducts.map((item, idx) => {
                  const categoryName = stockCategories.find(c => c.id === item.categoryId)?.name || 'Outro';
                  const txState = txStates[item.id] || { addQty: '', subQty: '', motive: '' };
                  
                  // Calculate quantity preview
                  let futureQty = item.quantity;
                  if (txState.addQty.trim()) {
                    const parsed = parseInt(txState.addQty);
                    if (!isNaN(parsed)) futureQty += parsed;
                  }
                  if (txState.subQty.trim()) {
                    const parsed = parseInt(txState.subQty);
                    if (!isNaN(parsed)) futureQty -= parsed;
                  }

                  return (
                    <tr key={item.id} id={`stock-row-${item.id}`} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="py-3 px-4 font-mono text-zinc-400 text-xxs text-center">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-zinc-900 text-xs">{item.name}</div>
                        <div className="text-xxs text-zinc-400 font-normal mt-0.5">Preço Venda: {formatCurrencyValue(item.price)}</div>
                      </td>
                      <td className="py-3 px-4 font-normal text-zinc-500 text-xs">{categoryName}</td>
                      <td className="py-3 px-4">
                        <span className={`font-mono text-xs font-bold ${item.quantity < 10 ? 'text-red-600 bg-red-50/50 px-1.5 py-0.5 rounded border border-red-100' : 'text-zinc-900'}`}>
                          {item.quantity} un
                        </span>
                      </td>
                      
                      {/* Adicionar Entry Column */}
                      <td className="py-3 px-4 pr-1">
                        <input
                          type="number"
                          id={`input-stock-add-${item.id}`}
                          disabled={!item.active}
                          value={txState.addQty}
                          onChange={(e) => {
                            // Clear sub quantity if typing entry
                            handleTxChange(item.id, 'addQty', e.target.value);
                            handleTxChange(item.id, 'subQty', '');
                          }}
                          className="w-16 lg:w-20 text-center text-xs p-1.5 border border-zinc-200 rounded-md focus:bg-white bg-zinc-50/50 focus:outline-none disabled:bg-zinc-100 disabled:text-zinc-400"
                          placeholder="Qtd"
                        />
                      </td>

                      {/* Retirar Exit Column */}
                      <td className="py-3 px-4 pr-1">
                        <input
                          type="number"
                          id={`input-stock-sub-${item.id}`}
                          disabled={!item.active}
                          value={txState.subQty}
                          onChange={(e) => {
                            // Clear add quantity if typing exit
                            handleTxChange(item.id, 'subQty', e.target.value);
                            handleTxChange(item.id, 'addQty', '');
                          }}
                          className="w-16 lg:w-20 text-center text-xs p-1.5 border border-zinc-200 rounded-md focus:bg-white bg-zinc-50/50 focus:outline-none disabled:bg-zinc-100"
                          placeholder="Qtd"
                        />
                      </td>

                      {/* Future calculation column */}
                      <td className="py-3 px-4">
                        <span className={`font-mono text-xs font-semibold ${
                          futureQty !== item.quantity 
                            ? (futureQty > item.quantity ? 'text-green-600 font-bold bg-green-50 px-1 py-0.5 rounded' : 'text-amber-600 font-bold bg-amber-50 px-1 py-0.5 rounded') 
                            : 'text-zinc-400'
                        }`}>
                          {futureQty} un
                        </span>
                      </td>

                      {/* Motivo */}
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          id={`input-stock-motive-${item.id}`}
                          disabled={!item.active || !txState.subQty}
                          value={txState.motive}
                          onChange={(e) => handleTxChange(item.id, 'motive', e.target.value)}
                          className="w-32 lg:w-40 text-xs p-1.5 border border-zinc-200 rounded bg-zinc-50/20 focus:bg-white focus:outline-none disabled:bg-zinc-100"
                          placeholder="Insira o motivo..."
                        />
                      </td>

                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center">
                          {item.active ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase">Ativo</span>
                          ) : (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 uppercase">Inativo</span>
                          )}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          id={`btn-toggle-stock-active-${item.id}`}
                          onClick={() => {
                            if (!isAdmin) {
                              alert("Apenas administradores podem desativar produtos.");
                              return;
                            }
                            onUpdateStockItem(item.id, { active: !item.active });
                          }}
                          className="text-zinc-400 hover:text-zinc-700 cursor-pointer"
                        >
                          {item.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List View for Stock */}
        <div className="md:hidden divide-y divide-zinc-200/80">
          {filteredProducts.length === 0 ? (
            <div id="stock-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
              <p className="mb-3">Nenhum insumo ou item de estoque correspondente.</p>
              <button onClick={openAddItemModal} className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold rounded-lg cursor-pointer">Adicionar primeiro item</button>
            </div>
          ) : (
            filteredProducts.map((item, idx) => {
              const categoryName = stockCategories.find(c => c.id === item.categoryId)?.name || 'Outro';
              const txState = txStates[item.id] || { addQty: '', subQty: '', motive: '' };
              
              // Calculate quantity preview
              let futureQty = item.quantity;
              if (txState.addQty.trim()) {
                const parsed = parseInt(txState.addQty);
                if (!isNaN(parsed)) futureQty += parsed;
              }
              if (txState.subQty.trim()) {
                const parsed = parseInt(txState.subQty);
                if (!isNaN(parsed)) futureQty -= parsed;
              }

              return (
                <div key={item.id} id={`stock-card-mobile-${item.id}`} className="p-4 bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-zinc-900 text-sm">{item.name}</div>
                      <div className="text-[10px] text-zinc-400 font-normal mt-0.5">Preço: {formatCurrencyValue(item.price)} | Categoria: {categoryName}</div>
                    </div>
                    <span className="text-xxs font-mono text-zinc-400 shrink-0">#{idx + 1}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-[11px] bg-zinc-50 p-2.5 rounded-lg border border-zinc-150">
                    <div>
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Insumo Atual</span>
                      <div className={`font-mono text-xs font-bold ${item.quantity < 10 ? 'text-red-600' : 'text-zinc-900'}`}>
                        {item.quantity} un
                      </div>
                    </div>
                    <div>
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Expectativa</span>
                      <div className={`font-mono text-xs font-bold ${
                        futureQty !== item.quantity 
                          ? (futureQty > item.quantity ? 'text-green-600 px-1 py-0.5 rounded bg-green-50' : 'text-amber-600 px-1 py-0.5 rounded bg-amber-50') 
                          : 'text-zinc-400'
                      }`}>
                        {futureQty} un
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor={`input-mobilestock-add-${item.id}`} className="block text-[9px] font-bold text-zinc-500 uppercase mb-1">Estocar (+ Entrada)</label>
                      <input
                        type="number"
                        id={`input-mobilestock-add-${item.id}`}
                        disabled={!item.active}
                        value={txState.addQty}
                        onChange={(e) => {
                          handleTxChange(item.id, 'addQty', e.target.value);
                          handleTxChange(item.id, 'subQty', '');
                        }}
                        className="w-full text-center text-xs p-1.5 border border-zinc-200 rounded-md focus:bg-white bg-zinc-50 focus:outline-none disabled:bg-zinc-100 disabled:text-zinc-400"
                        placeholder="Entrada Qtd"
                      />
                    </div>
                    <div>
                      <label htmlFor={`input-mobilestock-sub-${item.id}`} className="block text-[9px] font-bold text-zinc-500 uppercase mb-1">Debitar (- Saída)</label>
                      <input
                        type="number"
                        id={`input-mobilestock-sub-${item.id}`}
                        disabled={!item.active}
                        value={txState.subQty}
                        onChange={(e) => {
                          handleTxChange(item.id, 'subQty', e.target.value);
                          handleTxChange(item.id, 'addQty', '');
                        }}
                        className="w-full text-center text-xs p-1.5 border border-zinc-200 rounded-md focus:bg-white bg-zinc-50 focus:outline-none disabled:bg-zinc-100 disabled:text-zinc-400"
                        placeholder="Saída Qtd"
                      />
                    </div>
                  </div>

                  {txState.subQty && (
                    <div>
                      <label htmlFor={`input-mobilestock-motive-${item.id}`} className="block text-[9px] font-bold text-zinc-500 uppercase mb-1">Justificativa da Saída</label>
                      <input
                        type="text"
                        id={`input-mobilestock-motive-${item.id}`}
                        value={txState.motive}
                        onChange={(e) => handleTxChange(item.id, 'motive', e.target.value)}
                        className="w-full text-xs p-1.5 border border-zinc-200 rounded focus:bg-white bg-zinc-50 focus:outline-none"
                        placeholder="Insira o motivo..."
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-zinc-100 pt-2.5">
                    <div>
                      {item.active ? (
                        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase border border-green-200">Ativo</span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 uppercase border border-red-200">Inativo</span>
                      )}
                    </div>
                    <div>
                      <button
                        type="button"
                        id={`btn-mobile-toggle-stock-active-${item.id}`}
                        onClick={() => {
                          if (!isAdmin) {
                            alert("Apenas administradores podem desativar produtos.");
                            return;
                          }
                          onUpdateStockItem(item.id, { active: !item.active });
                        }}
                        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-700 transition-colors cursor-pointer"
                      >
                        <span className="text-xxs font-bold uppercase text-zinc-400">Ativo?</span>
                        {item.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bulk action buttons at the bottom of the table, matching page 6 & 7 instructions */}
        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xxs text-zinc-400 font-medium leading-normal block max-w-sm">
            * Insira as quantidades desejadas no formulário (desktop ou mobile) para realizar as movimentações de entrada ou saída corporativas.
          </span>
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              id="btn-stock-withdraw-submit"
              onClick={commitWithdrawals}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 border border-zinc-200 hover:border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 rounded-lg text-xs font-bold uppercase cursor-pointer transition-all shadow-sm active:scale-[0.98]"
            >
              <ArrowDownCircle className="w-4 h-4 text-amber-600" /> Lote Saída
            </button>
            <button
              type="button"
              id="btn-stock-add-submit"
              onClick={commitEntries}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase cursor-pointer transition-all shadow-sm shadow-green-900/10 active:scale-[0.98]"
            >
              <ArrowUpCircle className="w-4 h-4" /> Lote Entrada
            </button>
          </div>
        </div>
      </div>

      {/* Modal: MANAGE STOCK CATEGORIES */}
      {showCategoryModal && (
        <div id="cat-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-sm lg:max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-zinc-300" />
                <h3 className="text-xs font-bold uppercase tracking-wider">Categorias de Estoque</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Category form */}
              <form onSubmit={handleAddCategory} className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    id="input-new-cat-name"
                    value={newCatName}
                    onChange={(e) => {
                      setNewCatName(e.target.value);
                      setCatError('');
                    }}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Nova categoria (Ex. Bebidas, Salgados)"
                    required
                  />
                  {catError && (
                    <span className="text-xxs text-red-600 mt-1 block font-medium">{catError}</span>
                  )}
                </div>
                <button
                  type="submit"
                  id="btn-register-cat-submit"
                  className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98]"
                >
                  Cadastrar
                </button>
              </form>

              {/* Category lists */}
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                <span className="block text-xxs font-bold text-zinc-400 uppercase tracking-wider border-b border-zinc-100 pb-1.5">Categorias Existentes:</span>
                {stockCategories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-2 bg-zinc-50 hover:bg-zinc-100 placeholder border border-zinc-200/50 rounded-lg text-xs transition-colors">
                    <span className="font-semibold text-zinc-800">{cat.name}</span>
                    <div className="flex items-center gap-2">
                      {cat.active ? (
                        <span className="text-[10px] font-bold text-green-700 uppercase">Ativo</span>
                      ) : (
                        <span className="text-[10px] font-semibold text-zinc-400 uppercase">Inativo</span>
                      )}
                      
                      <button
                        type="button"
                        onClick={() => {
                          if (!isAdmin) {
                            alert("Apenas administradores podem gerenciar categorias de estoque.");
                            return;
                          }
                          onUpdateCategory(cat.id, !cat.active);
                        }}
                        className={`text-xxs border px-2 py-0.5 rounded cursor-pointer ${
                          cat.active 
                            ? 'text-green-600 border-green-100 hover:bg-green-50' 
                            : 'text-zinc-400 border-zinc-200 hover:bg-zinc-100'
                        }`}
                      >
                        {cat.active ? "Desativar" : "Ativar"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ADD STOCK ITEM (WITH MULTI-ADD / BULK OPTION AS PER PAGE 6) */}
      {showAddItemModal && (
        <div id="add-stock-item-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-2xl xl:max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Adicionar item ao estoque</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddItemModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitBulkItems} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {modalError && (
                <div className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                  {modalError}
                </div>
              )}

              {/* Rows List */}
              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2 pb-2">
                {newItemRows.map((row, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row items-end gap-3.5 p-3.5 bg-zinc-50/70 border border-zinc-200/50 rounded-xl relative group">
                    {/* Index */}
                    <span className="absolute left-2.5 top-2 text-[9px] font-extrabold text-zinc-300">#{idx + 1}</span>
                    
                    {/* Select Category */}
                    <div className="flex-1 w-full">
                      <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Categoria</label>
                      <select
                        value={row.categoryId}
                        onChange={(e) => handleItemRowChange(idx, 'categoryId', e.target.value)}
                        className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-white focus:outline-none"
                      >
                        {stockCategories.filter(c => c.active).map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Input Name */}
                    <div className="flex-[2] w-full sm:w-[40%]">
                      <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Nome do Item</label>
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => handleItemRowChange(idx, 'name', e.target.value)}
                        className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-white focus:outline-none"
                        placeholder="Ex: Coca-Cola Lata 350ml"
                        required
                      />
                    </div>

                    {/* Input Qty */}
                    <div className="w-full sm:w-[15%]">
                      <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Quantidade</label>
                      <input
                        type="number"
                        value={row.quantity}
                        onChange={(e) => handleItemRowChange(idx, 'quantity', e.target.value)}
                        className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-white focus:outline-none text-center"
                        required
                      />
                    </div>

                    {/* Input Price */}
                    <div className="w-full sm:w-[15%]">
                      <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Valor Unitário</label>
                      <input
                        type="number"
                        step="0.01"
                        value={row.price}
                        onChange={(e) => handleItemRowChange(idx, 'price', e.target.value)}
                        className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-white focus:outline-none text-center"
                        required
                      />
                    </div>

                    {/* Input MinLimit */}
                    <div className="w-full sm:w-[15%]">
                      <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wide mb-1">Limite Mínimo</label>
                      <input
                        type="number"
                        value={row.minLimit}
                        onChange={(e) => handleItemRowChange(idx, 'minLimit', e.target.value)}
                        className="w-full text-xs py-2 px-2 border border-zinc-200 rounded-lg text-zinc-800 bg-white focus:outline-none text-center"
                        required
                      />
                    </div>

                    {/* Remove button */}
                    {newItemRows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRowInModal(idx)}
                        className="p-2 border border-red-200 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer sm:mb-0.5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add New item field button */}
              <button
                type="button"
                id="btn-stock-add-new-row-field"
                onClick={handleAddNewRowInModal}
                className="w-full py-2.5 border border-dashed border-zinc-300 hover:border-zinc-500 rounded-lg text-zinc-600 hover:text-zinc-900 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer bg-zinc-50/20"
              >
                <Plus className="w-3.5 h-3.5" /> + Adicionar novo item
              </button>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddItemModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-stock-bulk-save"
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm shadow-green-950/10"
                >
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
